import { useEffect, useState } from 'react'
import { loadGoogleApiScripts, initializeDriveTokenClient, initializeGapiClient } from 'api/services/GoogleApi'
import { defaultQueryString } from 'components/ImportModal/utils'
import { GoogleDriveFile } from 'components/ImportModal/ImportFileRow'
import { uniqBy } from 'lodash'

interface EmailAndFiles {
  email: string
  files: any[]
  filesLoaded: boolean
}

interface GoogleDriveInputs {
  queryString: string
  reload: boolean
  searchAgain: boolean
  selectedFiles: GoogleDriveFile[]
}

export const useGoogleDrive = ({ reload, searchAgain, queryString, selectedFiles }: GoogleDriveInputs) => {
  const [filesLoaded, setFilesLoaded] = useState(false)
  const [isScriptLoaded, setIsScriptLoaded] = useState<Boolean>(false)
  const [files, setFiles] = useState<any[]>([])
  const [email, setEmail] = useState('')

  useEffect(() => {
    const loadGoogleScripts = async () => {
      await loadGoogleApiScripts()
      setIsScriptLoaded(true)
    }

    loadGoogleScripts()
  }, [])

  useEffect(() => {
    if (!isScriptLoaded) return

    const loadGoogleFiles = async () => {
      if (reload) {
        const tokenClient = initializeDriveTokenClient(async () => {
          await getFileList()
          await getProfile()
        })

        await initializeGapiClient()

        tokenClient.requestAccessToken({ prompt: 'consent' })
      } else if (searchAgain) {
        await getFileList()
        await getProfile()
      }
    }

    const sanitizeFiles = (files: any) => {
      let sanitizedFiles: { id: string; name: string; modifiedTime: string }[] = []

      files.forEach((file: any) => {
        let fileObj = {
          id: '',
          name: '',
          modifiedTime: '',
        }

        fileObj.id = file.id
        fileObj.name = file.name
        fileObj.modifiedTime = file.modifiedTime

        sanitizedFiles.push(fileObj)
      })

      return sanitizedFiles
    }

    const getProfile = async () => {
      const profileData = await gapi.client.people.people.get({
        resourceName: 'people/me',
        personFields: 'emailAddresses',
      })

      const emailValue = profileData?.result?.emailAddresses?.[0]?.value
      if (emailValue) setEmail(emailValue)
    }

    const getFileList = async () => {
      setFilesLoaded(false)

      const files = await gapi.client.drive.files.list({
        pageSize: 1000,
        fields: 'files(id, name, modifiedTime)',
        q: reload ? defaultQueryString : queryString,
      })

      if (!files.result?.files) {
        setFiles([])
      } else {
        const sanitizedFiles = sanitizeFiles(files.result.files)
        const newFiles = uniqBy(sanitizedFiles.concat(selectedFiles), 'id')
        setFiles(newFiles)
      }

      setFilesLoaded(true)

      return
    }

    loadGoogleFiles()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptLoaded, searchAgain, reload])

  return { filesLoaded, email, files } as EmailAndFiles
}
